import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { MiIcon } from "@miplatform/components";
import { ICalendarSet } from "@miplatform/types";
import { mdiPlus } from "@mdi/js";
import { Box, Grid, TextField, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  IWorkOrderResourceAssignment,
  IWorkOrderTemplateData,
} from "../ConfigWorkOrderTemplates.types";
import {
  matchesAssignmentUser,
  sortAssignmentsByDisplayOrder,
} from "../configWorkOrderTemplates.util";

export const WorkOrderSchedulingRulesTabView: FC<
  WorkOrderSchedulingRulesTabViewProps
> = ({
  form,
  extraTabProps: { isReadOnly, calendarSets = [] } = {
    isReadOnly: false,
    calendarSets: [],
  },
}) => {
  const theme = useTheme();
  const { watch, setValue } = form;
  const {
    autoAssign,
    orderTemplateId,
    calendarSetId,
    resourceAssignments = [],
  } = watch();

  const selectedCalendarSet = useMemo(
    () => calendarSets.find((calendarSet) => calendarSet.id === calendarSetId),
    [calendarSets, calendarSetId]
  );

  const assignableUsers = selectedCalendarSet?.assignableUsers ?? [];
  const hasAssignableUsers = assignableUsers.length > 0;
  const canConfigureAutoAssign =
    Boolean(orderTemplateId) && Boolean(calendarSetId) && hasAssignableUsers;
  const autoAssignControlsDisabled =
    isReadOnly || !autoAssign || !canConfigureAutoAssign;
  const orderedAssignments = useMemo(
    () => sortAssignmentsByDisplayOrder(resourceAssignments ?? []),
    [resourceAssignments]
  );

  const getUserDisplayName = (user: (typeof assignableUsers)[number]) =>
    user.displayName ||
    (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : null) ||
    user.name ||
    user.email;

  const getMatchingAssignableUser = (resourceId: string) =>
    assignableUsers.find((user) => matchesAssignmentUser(resourceId, user));

  const setAssignments = (
    assignments: IWorkOrderResourceAssignment[],
    shouldDirty: boolean
  ) => {
    setValue("resourceAssignments", assignments, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const updateperformanceMultiplierPercentageAtIndex = (
    index: number,
    performanceMultiplierPercentage: number
  ) => {
    const assignmentToUpdate = orderedAssignments[index];
    if (!assignmentToUpdate) {
      return;
    }

    const updatedAssignments = orderedAssignments.map((assignment) =>
      assignment.resourceId === assignmentToUpdate.resourceId
        ? {
            ...assignment,
            performanceMultiplierPercentage,
          }
        : assignment
    );

    setAssignments(updatedAssignments, true);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ConfigAccordionHeader title="User Performance">
        <Grid container columns={4} spacing={2}>
          {orderedAssignments.map((entry, index) => {
            const assignableUser = getMatchingAssignableUser(entry.resourceId);
            const displayName = assignableUser
              ? getUserDisplayName(assignableUser)
              : "Assignable User";

            return (
              <Grid size={{ md: 2, lg: 1 }} key={entry.resourceId}>
                <Box display="flex" flexDirection="column" width="100%" gap={2}>
                  <Typography
                    fontSize={theme.fontSize.medium}
                    fontWeight={theme.fontWeight.semiBold}
                  >
                    {displayName}
                  </Typography>
                  <NumericFormat
                    customInput={TextField}
                    label="Performance Multiplier"
                    value={entry.performanceMultiplierPercentage ?? 0}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <MiIcon
                            path={mdiPlus}
                            color={theme.palette.grey.medium}
                            style={{
                              marginRight: theme.spacing(1),
                            }}
                          />
                        ),
                      },
                    }}
                    onValueChange={({ floatValue }: { floatValue?: number }) =>
                      updateperformanceMultiplierPercentageAtIndex(
                        index,
                        Math.min(100, floatValue ?? 0)
                      )
                    }
                    decimalScale={0}
                    allowNegative={false}
                    isAllowed={({ floatValue }: { floatValue?: number }) =>
                      floatValue === undefined || floatValue <= 100
                    }
                    suffix="%"
                    size="small"
                    disabled={autoAssignControlsDisabled}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </ConfigAccordionHeader>
    </Box>
  );
};

interface WorkOrderSchedulingRulesTabViewProps {
  form: UseFormReturn<IWorkOrderTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    calendarSets?: ICalendarSet[];
  };
}
